import type {
  FieldError,
  FieldErrors,
  FieldValues,
  Resolver,
} from 'react-hook-form'

import type { AnyObjectSchema } from 'yup'
import { ValidationError } from 'yup'

const REQUIRED_FIELD_MESSAGE = ''

const setNestedFormError = (
  target: Record<string, unknown>,
  path: string,
  value: FieldError,
) => {
  const keys = path.replace(/\[(\d+)\]/g, '.$1').split('.')

  keys.reduce<Record<string, unknown>>((current, key, index) => {
    if (index === keys.length - 1) {
      current[key] = value
      return current
    }

    const nextKey = keys[index + 1]
    const nextValue = current[key]

    if (!nextValue || typeof nextValue !== 'object') {
      current[key] = /^\d+$/.test(nextKey) ? [] : {}
    }

    return current[key] as Record<string, unknown>
  }, target)
}

const mapYupValidationErrorToFormErrors = <TFormValues extends FieldValues>(
  error: ValidationError,
): FieldErrors<TFormValues> => {
  const seenPaths = new Set<string>()
  const formErrors = error.inner.reduce(
    (acc, validationError) => {
      const fieldName = validationError.path

      if (!fieldName || seenPaths.has(fieldName)) {
        return acc
      }

      seenPaths.add(fieldName)
      setNestedFormError(acc, fieldName, {
        type: validationError.type ?? 'validation',
        message:
          validationError.type === 'required'
            ? REQUIRED_FIELD_MESSAGE
            : validationError.message,
      })

      return acc
    },
    {} as Record<string, unknown>,
  )

  return formErrors as FieldErrors<TFormValues>
}

export const createYupFormResolver =
  <TFormValues extends FieldValues>(
    schema: AnyObjectSchema,
  ): Resolver<TFormValues> =>
  async (values) => {
    try {
      const nextValues = await schema.validate(values, {
        abortEarly: false,
      })

      return {
        values: nextValues,
        errors: {},
      }
    } catch (error) {
      if (error instanceof ValidationError) {
        return {
          values: {},
          errors: mapYupValidationErrorToFormErrors<TFormValues>(error),
        }
      }

      return {
        values: {},
        errors: {},
      }
    }
  }
